'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface RackUnit {
  id: string;
  rack_nome: string;
  sede_id: string | null;
  posizione_u: number;
  altezza_u: number;
  tipo: string;
  nome_apparato: string;
  device_id: string | null;
  ip_management: string;
  numero_seriale: string;
  note: string;
}

const tipoColors: Record<string, string> = {
  server: 'bg-sky-500/20 border-sky-500/30 text-sky-400',
  switch: 'bg-green-500/20 border-green-500/30 text-green-400',
  router: 'bg-violet-500/20 border-violet-500/30 text-violet-400',
  firewall: 'bg-red-500/20 border-red-500/30 text-red-400',
  patch_panel: 'bg-gray-500/20 border-gray-500/30 text-gray-400',
  ups: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
  kvm: 'bg-pink-500/20 border-pink-500/30 text-pink-400',
  storage: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
  vuoto: 'bg-transparent border-border/30 text-muted-color',
  altro: 'bg-gray-500/20 border-gray-500/30 text-gray-400',
};

const tipoIcons: Record<string, string> = {
  server: 'ServerIcon',
  switch: 'CircleStackIcon',
  router: 'GlobeAltIcon',
  firewall: 'ShieldCheckIcon',
  patch_panel: 'RectangleGroupIcon',
  ups: 'BoltIcon',
  kvm: 'ComputerDesktopIcon',
  storage: 'ArchiveBoxIcon',
  vuoto: 'MinusIcon',
  altro: 'CubeIcon',
};

export default function RackDiagramContent() {
  const [units, setUnits] = useState<RackUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [racks, setRacks] = useState<string[]>([]);
  const [selectedRack, setSelectedRack] = useState<string>('');
  const [selectedUnit, setSelectedUnit] = useState<RackUnit | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showNewRackModal, setShowNewRackModal] = useState(false);
  const [newRackName, setNewRackName] = useState('');
  const [form, setForm] = useState({ rack_nome: '', posizione_u: 1, altezza_u: 1, tipo: 'server', nome_apparato: '', ip_management: '', numero_seriale: '', note: '' });
  const [saving, setSaving] = useState(false);

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('rack_units').select('*').order('rack_nome').order('posizione_u');
      if (error) throw error;
      setUnits(data || []);
      const uniqueRacks = [...new Set((data || []).map((u: RackUnit) => u.rack_nome))];
      setRacks(uniqueRacks);
      if (uniqueRacks.length > 0 && !selectedRack) setSelectedRack(uniqueRacks[0]);
    } catch (err: any) {
      toast.error('Errore caricamento rack: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedRack]);

  useEffect(() => { fetchUnits(); }, []);

  const rackUnits = units.filter(u => u.rack_nome === selectedRack).sort((a, b) => a.posizione_u - b.posizione_u);
  const maxU = Math.max(...rackUnits.map(u => u.posizione_u + u.altezza_u - 1), 20);

  const handleSave = async () => {
    if (!form.rack_nome || !form.nome_apparato) { toast.error('Rack e nome apparato obbligatori'); return; }
    // Validate position overlap
    if (!selectedUnit) {
      const occupied = rackUnits.filter(u => {
        const uStart = u.posizione_u;
        const uEnd = u.posizione_u + u.altezza_u - 1;
        const newStart = form.posizione_u;
        const newEnd = form.posizione_u + form.altezza_u - 1;
        return !(newEnd < uStart || newStart > uEnd);
      });
      if (occupied.length > 0) {
        toast.error(`Posizione U${form.posizione_u} già occupata da "${occupied[0].nome_apparato}"`);
        return;
      }
    }
    setSaving(true);
    try {
      if (selectedUnit) {
        const { error } = await supabase.from('rack_units').update(form).eq('id', selectedUnit.id);
        if (error) throw error;
        toast.success('Apparato aggiornato');
      } else {
        const { error } = await supabase.from('rack_units').insert(form);
        if (error) throw error;
        toast.success('Apparato aggiunto al rack');
      }
      setShowModal(false);
      fetchUnits();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRack = () => {
    if (!newRackName.trim()) { toast.error('Nome rack obbligatorio'); return; }
    const name = newRackName.trim();
    if (racks.includes(name)) { toast.error('Rack già esistente'); return; }
    setRacks(prev => [...prev, name]);
    setSelectedRack(name);
    setNewRackName('');
    setShowNewRackModal(false);
    toast.success(`Rack "${name}" creato`);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminare questo apparato dal rack?')) return;
    try {
      const { error } = await supabase.from('rack_units').delete().eq('id', id);
      if (error) throw error;
      toast.success('Apparato rimosso');
      fetchUnits();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const openAdd = () => {
    setSelectedUnit(null);
    setForm({ rack_nome: selectedRack, posizione_u: maxU + 1, altezza_u: 1, tipo: 'server', nome_apparato: '', ip_management: '', numero_seriale: '', note: '' });
    setShowModal(true);
  };

  const openEdit = (u: RackUnit) => {
    setSelectedUnit(u);
    setForm({ rack_nome: u.rack_nome, posizione_u: u.posizione_u, altezza_u: u.altezza_u, tipo: u.tipo, nome_apparato: u.nome_apparato, ip_management: u.ip_management, numero_seriale: u.numero_seriale, note: u.note });
    setShowModal(true);
  };

  // Build rack grid
  const occupiedSlots = new Map<number, RackUnit>();
  rackUnits.forEach(u => {
    for (let i = 0; i < u.altezza_u; i++) {
      occupiedSlots.set(u.posizione_u + i, u);
    }
  });

  return (
    <div className="flex flex-col h-full bg-background overflow-auto scrollbar-thin">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Diagramma Rack</h1>
            <p className="text-sm text-secondary-color mt-0.5">Posizione fisica apparati U per U nella server room</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowNewRackModal(true)} className="flex items-center gap-2 px-4 py-2 bg-surface border border-border text-secondary-color rounded-lg text-sm font-semibold hover:text-white hover:bg-surface-elevated transition-all">
              <Icon name="PlusIcon" size={16} />
              Nuovo Rack
            </button>
            <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all">
              <Icon name="PlusIcon" size={16} />
              Aggiungi Apparato
            </button>
          </div>
        </div>
      </div>

      {/* New Rack Modal */}
      {showNewRackModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Crea Nuovo Rack</h3>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-2">Nome Rack *</label>
              <input
                value={newRackName}
                onChange={e => setNewRackName(e.target.value)}
                placeholder="es. RACK-01, Server Room A"
                className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-sky-500"
                onKeyDown={e => e.key === 'Enter' && handleCreateRack()}
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowNewRackModal(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleCreateRack} className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-sm font-bold transition-all">Crea Rack</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Rack selector */}
        <div className="w-48 flex-shrink-0 border-r border-border bg-surface overflow-y-auto scrollbar-thin p-3">
          <p className="text-xs font-bold text-muted-color uppercase tracking-widest mb-3 px-2">Rack</p>
          {racks.map(r => (
            <button key={r} onClick={() => setSelectedRack(r)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all mb-1 ${selectedRack === r ? 'bg-sky-500/10 text-sky-400' : 'text-secondary-color hover:bg-surface-elevated hover:text-white'}`}>
              <Icon name="RectangleGroupIcon" size={14} className="inline mr-2" />
              {r}
            </button>
          ))}
        </div>

        {/* Rack diagram */}
        <div className="flex-1 p-6 overflow-auto scrollbar-thin">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !selectedRack ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Icon name="RectangleGroupIcon" size={40} className="text-muted-color" />
              <p className="text-secondary-color font-semibold">Nessun rack disponibile</p>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">{selectedRack}</h2>
                <span className="text-sm text-muted-color">{rackUnits.filter(u => u.tipo !== 'vuoto').length} apparati · {maxU}U totali</span>
              </div>

              {/* Rack visual */}
              <div className="bg-surface border-2 border-border rounded-xl overflow-hidden">
                {/* Top panel */}
                <div className="bg-surface-elevated border-b border-border px-4 py-2 flex items-center gap-2">
                  <div className="flex gap-1">
                    {[...Array(4)].map((_, i) => <div key={i} className="w-2 h-2 rounded-full bg-border" />)}
                  </div>
                  <span className="text-xs font-mono text-muted-color ml-2">{selectedRack}</span>
                </div>

                {/* U slots */}
                <div className="p-2 space-y-0.5">
                  {[...Array(maxU)].map((_, idx) => {
                    const u = idx + 1;
                    const unit = occupiedSlots.get(u);
                    const isFirst = unit && unit.posizione_u === u;
                    const isOccupied = !!unit;

                    if (isOccupied && !isFirst) return null;

                    return (
                      <div
                        key={u}
                        style={{ height: isFirst ? `${unit!.altezza_u * 28}px` : '28px' }}
                        className={`flex items-center gap-2 rounded px-2 border transition-all cursor-pointer ${isFirst ? `${tipoColors[unit!.tipo]} hover:opacity-80` : 'border-border/20 hover:bg-surface-elevated/30'}`}
                        onClick={() => isFirst && openEdit(unit!)}
                      >
                        <span className="text-xs font-mono text-muted-color w-6 flex-shrink-0">{u}U</span>
                        {isFirst ? (
                          <>
                            <Icon name={tipoIcons[unit!.tipo] as any || 'CubeIcon'} size={14} className="flex-shrink-0" />
                            <span className="text-xs font-semibold truncate flex-1">{unit!.nome_apparato}</span>
                            {unit!.ip_management && <span className="text-xs font-mono opacity-60 hidden sm:block">{unit!.ip_management}</span>}
                            <span className="text-xs opacity-50">{unit!.altezza_u}U</span>
                            <button onClick={e => { e.stopPropagation(); handleDelete(unit!.id); }} className="p-0.5 rounded hover:bg-red-500/20 hover:text-red-400 transition-colors flex-shrink-0">
                              <Icon name="XMarkIcon" size={12} />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-muted-color italic">vuoto</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Bottom panel */}
                <div className="bg-surface-elevated border-t border-border px-4 py-2 flex items-center gap-2">
                  <div className="flex gap-1">
                    {[...Array(4)].map((_, i) => <div key={i} className="w-2 h-2 rounded-full bg-border" />)}
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(tipoColors).filter(([k]) => k !== 'vuoto').map(([tipo, cls]) => (
                  <span key={tipo} className={`px-2 py-1 rounded text-xs font-semibold border ${cls} capitalize`}>{tipo.replace('_', ' ')}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">{selectedUnit ? 'Modifica Apparato' : 'Aggiungi Apparato'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Rack *</label>
                  <input type="text" value={form.rack_nome} onChange={e => setForm({ ...form, rack_nome: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tipo</label>
                  <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                    {Object.keys(tipoColors).map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Nome Apparato *</label>
                <input type="text" value={form.nome_apparato} onChange={e => setForm({ ...form, nome_apparato: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Posizione U</label>
                  <input type="number" min={1} value={form.posizione_u} onChange={e => setForm({ ...form, posizione_u: parseInt(e.target.value) || 1 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Altezza (U)</label>
                  <input type="number" min={1} max={10} value={form.altezza_u} onChange={e => setForm({ ...form, altezza_u: parseInt(e.target.value) || 1 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">IP Management</label>
                  <input type="text" value={form.ip_management} onChange={e => setForm({ ...form, ip_management: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">N. Seriale</label>
                  <input type="text" value={form.numero_seriale} onChange={e => setForm({ ...form, numero_seriale: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
